import React from 'react';
import Button from '@amzn/awsui-components-core/button';
import Checkbox from '@amzn/awsui-components-core/checkbox';
import type { StudioVarianceBlock } from 'src/components/star-grid/reportStudio.types';
import type { ReportStudioState } from 'src/components/star-grid/hooks/useReportStudio';
import type { useStudioColumn } from 'src/components/star-grid/hooks/useStudioColumn';
import { StudioCompactSelect } from 'src/components/star-grid/components/StudioCompactSelect';
import styles from 'src/components/star-grid/components/styles/ReportStudio.module.scss';
interface Props {
  readonly compact?: boolean;
  readonly column: StudioVarianceBlock;
  readonly state: ReportStudioState;
  readonly edit: ReturnType<typeof useStudioColumn>;
}
export const StudioVarianceEditor: React.FC<Props> = ({ column, state, edit, compact }) => (
  <>
    <div className={compact ? styles.comparisonControls : styles.fxRow}>
      <label className={styles.comparisonControl}>
        <span>{state.text('studioBase')}</span>
        <StudioCompactSelect
          label={state.text('studioBase')}
          value={column.base_block_id}
          options={edit.options.filter((option) => option.id !== column.compare_block_id)}
          onChange={(base_block_id) => state.updateColumn({ ...column, base_block_id, period_pairs: [] })}
        />
      </label>
      <label className={styles.comparisonControl}>
        <span>{state.text('studioDirection')}</span>
        <StudioCompactSelect
          label={state.text('studioDirection')}
          value={column.direction}
          options={(['base_minus_compare', 'compare_minus_base', 'favorable'] as const).map((id) => ({
            id,
            label: state.text(`studioDirection_${id}`)
          }))}
          onChange={(direction) => state.updateColumn({ ...column, direction: direction as StudioVarianceBlock['direction'] })}
        />
      </label>
      <label className={styles.comparisonControl}>
        <span>{state.text('studioCompare')}</span>
        <StudioCompactSelect
          label={state.text('studioCompare')}
          value={column.compare_block_id}
          options={edit.options.filter((option) => option.id !== column.base_block_id)}
          onChange={(compare_block_id) => state.updateColumn({ ...column, compare_block_id, period_pairs: [] })}
        />
      </label>
    </div>
    <div className={styles.presets}>
      {(['same', 'prior_year', 'prior_period'] as const).map((mode) => (
        <button type="button" key={mode} onClick={() => edit.mapPeriods(mode)}>
          {state.text(`studioMap_${mode}`)}
        </button>
      ))}
    </div>
    <div className={styles.mappingWrap}>
      <table className={styles.mapping}>
        <thead>
          <tr>
            <th>{state.text('studioBase')}</th>
            <th>{state.text('studioCompare')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {column.period_pairs.map((pair, index) => (
            <tr key={index}>
              <td>
                <StudioCompactSelect
                  label={state.text('studioBasePeriod')}
                  value={pair.base}
                  options={state.template.catalog.periods.filter((item) => edit.base?.period_ids.includes(item.id))}
                  onChange={(base) =>
                    state.updateColumn({ ...column, period_pairs: column.period_pairs.map((item, i) => (i === index ? { ...item, base } : item)) })
                  }
                />
              </td>
              <td>
                <StudioCompactSelect
                  label={state.text('studioComparePeriod')}
                  value={pair.compare}
                  options={state.template.catalog.periods.filter((item) => edit.compare?.period_ids.includes(item.id))}
                  onChange={(compare) =>
                    state.updateColumn({ ...column, period_pairs: column.period_pairs.map((item, i) => (i === index ? { ...item, compare } : item)) })
                  }
                />
              </td>
              <td>
                <Button
                  variant="icon"
                  iconName="close"
                  ariaLabel={state.text('studioRemovePair')}
                  onClick={() => state.updateColumn({ ...column, period_pairs: column.period_pairs.filter((_, i) => i !== index) })}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className={styles.fxRow}>
      <span>{state.text('studioFX')}</span>
      <StudioCompactSelect
        label={state.text('studioFX')}
        value={column.fx_policy_id ?? ''}
        options={[{ id: '', label: state.text('studioDefaultFX') }, ...state.dataset.fx_policies]}
        onChange={(fx_policy_id) => state.updateColumn({ ...column, fx_policy_id: fx_policy_id || null })}
      />
    </div>
    <div className={styles.fxRow}>
      <Checkbox checked={Boolean(column.show_percent)} onChange={({ detail }) => state.updateColumn({ ...column, show_percent: detail.checked })}>
        {state.text('studioShowPercent')}
      </Checkbox>
    </div>
  </>
);
